import { useEffect, useState } from "react";
import { wishTexts } from "../texts/wishes";

interface CardProps extends DarkMode, MobileMode, OrientationMode {
  wish: string;
  isModal: boolean;
}

const WishCard = ({
  isMobile,
  isDark,
  isVertical,
  wish,
  isModal,
}: CardProps) => {
  const [isFlipping, setIsFlipping] = useState(false);
  const [isPhoto, setIsPhoto] = useState(true);
  const [isReversed, setIsReversed] = useState(false);

  const photoAnimation = `${isPhoto ? "" : "hidden"} 
  ${isFlipping ? `${isReversed ? "flip-reverse" : "flip"}` : ""} `;

  const wishAnimation = `${
    isPhoto
      ? "hidden"
      : "flex items-center justify-center text-center bg-light-header"
  } 
  ${isFlipping ? `${isReversed ? "flip" : "flip-reverse"}` : ""}`;

  const setFlippingTimeout = () => {
    setIsFlipping(true);
    setTimeout(() => setIsPhoto(!isPhoto), 250);
    setTimeout(() => setIsFlipping(false), 500);
    setTimeout(() => setIsReversed(!isReversed), 500);
  };

  const smallerSide = window.visualViewport![isVertical ? "width" : "height"];
  const mobileMultiplier = smallerSide < 670 ? 0.8 : 1.15;

  const controlSide = isVertical ? window.innerHeight : window.innerWidth;

  const desktopMultiplier =
    controlSide >= 1536
      ? 1.75
      : controlSide >= 1280
      ? 1.25
      : controlSide > 1024
      ? 1.1
      : 0.9;

  const multiplier = isMobile
    ? mobileMultiplier * 0.32
    : desktopMultiplier * 0.1171;

  const notModalWidth = smallerSide * multiplier;

  const modalWidth = isVertical ? smallerSide * 0.8 : smallerSide * 0.65;

  const notModalHeight = 1.235 * notModalWidth;

  const modalHeight = modalWidth * 1.235;

  return (
    <div style={wish.includes("empty") ? { height: notModalHeight } : {}}>
      <img
        className={photoAnimation}
        src={`/wishes/${wish}.jpg`}
        alt={wish}
        onClick={() => setFlippingTimeout()}
        width={isModal ? modalWidth : notModalWidth}
      />
      <div
        className={`px-2 ${isDark ? "text-black" : ""} 
        ${wishAnimation} ${
          !isModal && (isVertical || isMobile)
            ? "text-xs"
            : isModal && isMobile
            ? "text-md"
            : isModal
            ? "text-3xl"
            : "md:text-xs lg:text-md xl:text-lg"
        }`}
        style={{
          width: isModal ? modalWidth : notModalWidth,
          height: isModal ? modalHeight : notModalHeight,
        }}
        onClick={() => setFlippingTimeout()}
      >
        {wishTexts[wish]}
      </div>
    </div>
  );
};

export default function Wishes({
  isMobile,
  isVertical,
  isDark,
  setContent,
  setModalOpen,
}: PageProps) {
  const [mobileIndex, setMobileIndex] = useState(0);

  const isHorizontalMobile = isMobile && !isVertical;
  const isVerticalDesktop = !isMobile && isVertical;

  const cardNames = [
    "tanya",
    "mama-d",
    "varya",
    "liza-1",
    "iliya",
    "ksusha",
    "roditely",
    "nastya",
    "liza-2",
    ...(isHorizontalMobile || isVerticalDesktop
      ? ["empty1", "papa-a", "empty2"]
      : isMobile
      ? ["papa-a", "empty1", "empty2"]
      : ["papa-a"]),
  ];

  const rows =
    !isHorizontalMobile && !isVerticalDesktop ? 2 : isHorizontalMobile ? 1 : 4;

  const cols = isMobile ? (isVertical ? 2 : 3) : isVerticalDesktop ? 3 : 5;

  const wishesBlock = Array.from(
    { length: Math.ceil(cardNames.length / cols) },
    (_, i) => cardNames.slice(i * cols, i * cols + cols)
  );

  const leftArrowButton = (
    <button
      className={`p-1 rounded-full ${isDark ? "bg-gray-700" : "bg-red-200"}`}
      style={{
        visibility: !mobileIndex ? "hidden" : "visible",
      }}
      onClick={() => setMobileIndex(mobileIndex - (isVertical ? 2 : 1))}
    >
      <img src="/thanks/left-arrow.png" alt="previous" width={30} />
    </button>
  );

  const rightArrowButton = (
    <button
      className={`p-1 rounded-full ${isDark ? "bg-gray-700" : "bg-red-200"}`}
      style={{
        visibility: mobileIndex === (isVertical ? 4 : 3) ? "hidden" : "visible",
      }}
      onClick={() => setMobileIndex(mobileIndex + (isVertical ? 2 : 1))}
    >
      <img src="/thanks/right-arrow.png" alt="next" width={30} />
    </button>
  );

  useEffect(() => {
    if (!isVertical && mobileIndex === 4) {
      setMobileIndex(3);
    } else if (isVertical && mobileIndex === 3) {
      setMobileIndex(4);
    }
  }, [isVertical, mobileIndex, setMobileIndex]);

  return (
    <div
      className={`flex flex-col items-center 
    ${
      isMobile
        ? window.innerWidth < 321
          ? "pt-4 justify-between"
          : "pt-6 justify-between"
        : isVertical
        ? "sm:pt-8 tablet:pt-12 lg:pt-20 gap-2"
        : "pt-16 gap-2"
    }`}
    >
      <p
        className={
          isMobile || isVertical
            ? window.innerWidth < 321
              ? "text-lg"
              : "text-2xl"
            : "text-3xl pl-2 pb-8"
        }
      >
        Ваши пожелания
      </p>
      {Array.from({ length: rows }).map((_, row) => (
        <div
          key={row}
          className={`flex 
          ${window.innerWidth < 321 ? "pt-4" : "pt-6"} 
          ${
            isMobile
              ? `${window.innerWidth < 321 ? "gap-4" : "gap-6"} ${
                  isVertical ? "" : "items-center"
                }`
              : "gap-20"
          }`}
        >
          <div className={isHorizontalMobile ? "flex" : "hidden"}>
            {leftArrowButton}
          </div>
          {wishesBlock[isMobile ? mobileIndex + row : row]?.map((wish) => (
            <div
              key={wish}
              className={`flex flex-col items-center ${
                wish.includes("empty") ? "invisible" : ""
              }`}
            >
              <div
                className={`border-double border-8 p-1 ${
                  isDark ? "border-gray-500" : "border-red-200"
                }`}
              >
                <WishCard
                  isDark={isDark}
                  isMobile={isMobile}
                  isVertical={isVertical}
                  wish={wish}
                  isModal={false}
                />
              </div>
              <div className="pt-4">
                <button
                  className={`${isDark ? "bg-gray-700" : "bg-gray-50"} 
                  py-1 px-3 rounded-2xl text-center hover:bg-wine hover:text-white`}
                  onClick={() => {
                    setContent(
                      <WishCard
                        isDark={isDark}
                        isMobile={isMobile}
                        isVertical={isVertical}
                        wish={wish}
                        isModal={true}
                      />
                    );
                    setModalOpen(true);
                  }}
                >
                  Раскрыть
                </button>
              </div>
            </div>
          ))}
          <div className={isHorizontalMobile ? "flex" : "hidden"}>
            {rightArrowButton}
          </div>
        </div>
      ))}
      <div
        className={
          isMobile && !isHorizontalMobile ? "flex gap-12 pt-6" : "hidden"
        }
      >
        {leftArrowButton}
        {rightArrowButton}
      </div>
    </div>
  );
}
